import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Table,
  Badge,
  Spinner,
  Alert
} from "reactstrap"
import Chart from "react-apexcharts"
import {
  getRFQStatusAnalytics,
  getRFQDashboardOverview,
  getRFQsByStatus,
  formatCurrency,
  getStatusColor,
  getChartColors,
  processChartData,
  getAdditionalMilestonesAnalytics,
  getAdditionalTasksAnalytics
} from "../../../../helpers/rfq_dashboard_api"
import StatusCardModal from "./StatusCardModal"
import StatusDetailModal from "./StatusDetailModal"
import TimelineDetailModal from "./TimelineDetailModal"
import AdditionalMilestonesModal from "./AdditionalMilestonesModal"
import AdditionalTasksModal from "./AdditionalTasksModal"
import InfoButton from "./InfoButton"
import "./ClickableCards.css"

const StatusAnalytics = ({ filters, onRefresh }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [overviewData, setOverviewData] = useState(null)

  // Modal states
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedCardType, setSelectedCardType] = useState(null)
  const [statusModalOpen, setStatusModalOpen] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState(null)
  const [timelineModalOpen, setTimelineModalOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState(null)
  const [milestonesModalOpen, setMilestonesModalOpen] = useState(false)
  const [tasksModalOpen, setTasksModalOpen] = useState(false)

  // Additional analytics data
  const [milestonesData, setMilestonesData] = useState(null)
  const [tasksData, setTasksData] = useState(null)

  // Load status analytics data
  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      console.log("Loading status analytics with filters:", filters)

      // Call all APIs in parallel
      const [statusResponse, overviewResponse, totalResponse, createdResponse, wipResponse, closedResponse, milestonesResponse, tasksResponse] = await Promise.all([
        getRFQStatusAnalytics(filters),
        getRFQDashboardOverview(filters),
        getRFQsByStatus('total', { ...filters, page: 1, per_page: 1 }),
        getRFQsByStatus('created', { ...filters, page: 1, per_page: 1 }),
        getRFQsByStatus('wip', { ...filters, page: 1, per_page: 1 }),
        getRFQsByStatus('closed', { ...filters, page: 1, per_page: 1 }),
        getAdditionalMilestonesAnalytics(filters),
        getAdditionalTasksAnalytics(filters)
      ])

      console.log("API responses:", {
        status: statusResponse,
        overview: overviewResponse,
        individual: { totalResponse, createdResponse, wipResponse, closedResponse }
      })

      // Handle status analytics data
      if (statusResponse && statusResponse.status) {
        setData(statusResponse.data)
        console.log("Status analytics loaded successfully:", statusResponse.data)
      } else {
        console.error("Status analytics API failed:", statusResponse)
        throw new Error(statusResponse?.message || "Failed to load status analytics")
      }

      // Extract counts from individual APIs (these are most reliable)
      const totalCount = totalResponse?.data?.pagination?.total || 0
      const createdCount = createdResponse?.data?.pagination?.total || 0
      const wipCount = wipResponse?.data?.pagination?.total || 0
      const closedCount = closedResponse?.data?.pagination?.total || 0

      console.log("Individual API counts:", { totalCount, createdCount, wipCount, closedCount })

      // Create overview data prioritizing individual API counts
      const individualCounts = {
        total_rfqs: totalCount,
        created_rfqs: createdCount,
        work_in_progress_rfqs: wipCount,
        closed_rfqs: closedCount
      }

      // Use overview API data as base, but override with individual counts
      let finalOverviewData
      if (overviewResponse && overviewResponse.status) {
        finalOverviewData = {
          ...overviewResponse.data,
          summary: {
            ...overviewResponse.data?.summary,
            ...individualCounts // Individual counts take precedence
          }
        }
        console.log("Using overview API with individual count overrides:", finalOverviewData)
      } else {
        finalOverviewData = {
          summary: individualCounts
        }
        console.log("Using individual API counts only:", finalOverviewData)
      }

      setOverviewData(finalOverviewData)

      // Handle additional milestones data
      if (milestonesResponse && milestonesResponse.status) {
        setMilestonesData(milestonesResponse.data)
        console.log("Additional milestones analytics loaded successfully:", milestonesResponse.data)
      } else {
        console.error("Additional milestones API failed:", milestonesResponse)
        setMilestonesData({
          total_additional_milestones: 0,
          total_payment_amount: 0,
          pending_milestones: 0,
          completed_milestones: 0,
          in_progress_milestones: 0
        })
      }

      // Handle additional tasks data
      if (tasksResponse && tasksResponse.status) {
        setTasksData(tasksResponse.data)
        console.log("Additional tasks analytics loaded successfully:", tasksResponse.data)
      } else {
        console.error("Additional tasks API failed:", tasksResponse)
        setTasksData({
          total_additional_tasks: 0,
          pending_tasks: 0,
          completed_tasks: 0,
          in_progress_tasks: 0,
          total_work_progress: 0
        })
      }

    } catch (err) {
      console.error("Status analytics loading error:", err)
      setError(err.message || "Failed to load status analytics")

      // Fallback to mock data on error
      const mockData = {
        status_distribution: [
          { status_name: 'Created', count: 0, percentage: 0, avg_days_in_status: 0, total_budget: 0, total_final_amount: 0, earliest_rfq: null, latest_rfq: null },
          { status_name: 'In Progress', count: 0, percentage: 0, avg_days_in_status: 0, total_budget: 0, total_final_amount: 0, earliest_rfq: null, latest_rfq: null },
          { status_name: 'Completed', count: 0, percentage: 0, avg_days_in_status: 0, total_budget: 0, total_final_amount: 0, earliest_rfq: null, latest_rfq: null }
        ],
        status_timeline: [],
        total_rfqs: 0
      }
      setData(mockData)
      setOverviewData({
        summary: {
          total_rfqs: 0,
          work_in_progress_rfqs: 0,
          closed_rfqs: 0
        }
      })
    } finally {
      setLoading(false)
    }
  }

  // Load data when filters change
  useEffect(() => {
    loadData()
  }, [filters])

  // Handle card click
  const handleCardClick = (cardType) => {
    setSelectedCardType(cardType)
    setModalOpen(true)
  }

  // Handle status chart click
  const handleStatusChartClick = (event, chartContext, config) => {
    if (config.dataPointIndex >= 0) {
      const clickedStatus = status_distribution[config.dataPointIndex]
      if (clickedStatus) {
        console.log("Status chart clicked:", clickedStatus)
        console.log("Status name being sent:", clickedStatus.status_name)
        console.log("Full status object:", clickedStatus)
        console.log("Current filters being passed to modal:", filters)
        setSelectedStatus(clickedStatus)
        setStatusModalOpen(true)
      } else {
        console.log("No status found for index:", config.dataPointIndex)
        console.log("Status distribution array:", status_distribution)
      }
    }
  }

  // Handle timeline chart click
  const handleTimelineChartClick = (event, chartContext, config) => {
    console.log("Timeline chart click event:", { event, chartContext, config })

    // Handle different event types
    let dataPointIndex = -1

    if (config && typeof config.dataPointIndex !== 'undefined') {
      dataPointIndex = config.dataPointIndex
    } else if (config && typeof config.seriesIndex !== 'undefined' && typeof config.dataPointIndex !== 'undefined') {
      dataPointIndex = config.dataPointIndex
    }

    console.log("Timeline dataPointIndex:", dataPointIndex)
    console.log("Timeline array:", timelineArray)

    if (dataPointIndex >= 0 && dataPointIndex < timelineArray.length) {
      const clickedMonthData = timelineArray[dataPointIndex]
      if (clickedMonthData) {
        console.log("Timeline chart clicked successfully:", clickedMonthData)
        setSelectedDate(clickedMonthData) // Still using setSelectedDate but now contains month data
        setTimelineModalOpen(true)
      } else {
        console.log("No month data found for index:", dataPointIndex)
      }
    } else {
      console.log("Invalid dataPointIndex:", dataPointIndex, "Array length:", timelineArray.length)
    }
  }

  // Fallback click handler for timeline chart container
  const handleTimelineContainerClick = (event) => {
    console.log("Timeline container clicked")
    // If the chart click events aren't working, we can implement a fallback here
    // For testing, let's open the modal with the first date if available
    if (timelineArray.length > 0) {
      console.log("Opening timeline modal with first date:", timelineArray[0])
      setSelectedDate(timelineArray[0])
      setTimelineModalOpen(true)
    }
  }

  // Handle additional milestones card click
  const handleMilestonesCardClick = () => {
    console.log("Additional milestones card clicked")
    setMilestonesModalOpen(true)
  }

  // Handle additional tasks card click
  const handleTasksCardClick = () => {
    console.log("Additional tasks card clicked")
    setTasksModalOpen(true)
  }

  // Calculate card data
  const getCardData = () => {
    if (!data || !overviewData) {
      console.log("getCardData: Missing data or overviewData", { data, overviewData })
      return []
    }

    const summary = overviewData.summary || {}
    const totalRfqs = summary.total_rfqs || 0
    const wipRfqs = summary.work_in_progress_rfqs || 0
    const closedRfqs = summary.closed_rfqs || 0

    // Use created_rfqs if available from fallback, otherwise calculate
    const createdRfqs = summary.created_rfqs !== undefined
      ? summary.created_rfqs
      : Math.max(0, totalRfqs - closedRfqs - wipRfqs)

    console.log("getCardData: Card counts calculation", {
      totalRfqs,
      wipRfqs,
      closedRfqs,
      createdRfqs,
      calculatedCreated: Math.max(0, totalRfqs - closedRfqs - wipRfqs),
      usingFallback: summary.created_rfqs !== undefined,
      summary
    })

    return [
      {
        title: "Total RFQ",
        value: totalRfqs,
        color: "primary",
        icon: "ri-file-list-3-line",
        type: "total"
      },
      {
        title: "Created RFQ",
        value: createdRfqs,
        color: "success",
        icon: "ri-add-circle-line",
        type: "created"
      },
      {
        title: "Work in Progress RFQ",
        value: wipRfqs,
        color: "warning",
        icon: "ri-time-line",
        type: "wip"
      },
      {
        title: "Closed RFQ",
        value: closedRfqs,
        color: "info",
        icon: "ri-check-double-line",
        type: "closed"
      }
    ]
  }

  // Get additional cards data (milestones and tasks)
  const getAdditionalCardsData = () => {
    const cards = []

    // Additional Milestones Card
    if (milestonesData) {
      cards.push({
        title: "Additional Milestones",
        value: milestonesData.total_additional_milestones || 0,
        color: "purple",
        icon: "bx bx-task",
        type: "additional_milestones",
        subtitle: `₹${(milestonesData.total_payment_amount || 0).toLocaleString('en-IN')} Total Payment`,
        onClick: handleMilestonesCardClick
      })
    }

    // Additional Tasks Card
    if (tasksData) {
      cards.push({
        title: "Additional Tasks",
        value: tasksData.total_additional_tasks || 0,
        color: "orange",
        icon: "bx bx-list-check",
        type: "additional_tasks",
        subtitle: `${tasksData.total_work_progress || 0}% Avg Progress`,
        onClick: handleTasksCardClick
      })
    }

    return cards
  }

  // Loading state
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <div className="mt-3">
          <h5>Loading Status Analytics...</h5>
          <p className="text-muted">Please wait while we fetch the data</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <Alert color="danger" className="text-center">
        <h4 className="alert-heading">Error Loading Status Analytics</h4>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={loadData}>
          <i className="bx bx-refresh me-1"></i>
          Try Again
        </button>
      </Alert>
    )
  }

  if (!data || !data.status_distribution) {
    return (
      <div className="text-center py-5">
        <i className="bx bx-bar-chart-alt-2 display-4 text-muted"></i>
        <h5 className="mt-3">No Status Data Available</h5>
        <p className="text-muted">Please adjust your filters and try again.</p>
      </div>
    )
  }

  // Safe data destructuring with defaults
  const {
    status_distribution = [],
    status_timeline = [],
    total_rfqs = 0
  } = data || {}

  // Prepare chart data for status distribution - using multicolor bar chart
  const statusChartData = processChartData(status_distribution, 'status_name', 'count')

  // Create multicolor array - one color per bar
  const multiColors = [
    '#556ee6', // Primary Blue
    '#34c38f', // Success Green
    '#f1b44c', // Warning Yellow
    '#50a5f1', // Info Light Blue
    '#f46a6a', // Danger Red
    '#9c27b0', // Purple
    '#ff9800', // Orange
    '#795548', // Brown
    '#e91e63', // Pink
    '#00bcd4', // Cyan
    '#4caf50', // Light Green
    '#ff5722'  // Deep Orange
  ]

  const statusChartOptions = {
    chart: {
      type: 'bar',
      height: 400,
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false
        }
      },
      events: {
        dataPointSelection: handleStatusChartClick
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        endingShape: 'rounded',
        dataLabels: {
          position: 'top'
        },
        distributed: true // Enable distributed colors (different color for each bar)
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val
      },
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ["#304758"]
      }
    },
    xaxis: {
      categories: statusChartData.labels,
      position: 'bottom',
      axisBorder: {
        show: false
      },
      axisTicks: {
        show: false
      },
      crosshairs: {
        fill: {
          type: 'gradient',
          gradient: {
            colorFrom: '#D8E3F0',
            colorTo: '#BED1E6',
            stops: [0, 100],
            opacityFrom: 0.4,
            opacityTo: 0.5,
          }
        }
      },
      tooltip: {
        enabled: true,
      }
    },
    yaxis: {
      axisBorder: {
        show: false
      },
      axisTicks: {
        show: false,
      },
      labels: {
        show: true,
        formatter: function (val) {
          return val
        }
      }
    },
    colors: multiColors, // Use multicolor array
    grid: {
      borderColor: '#f1f1f1',
    },
    legend: {
      show: false // Hide legend since we have distributed colors
    },
    title: {
      text: 'RFQ Status Distribution',
      align: 'center',
      style: {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#263238'
      }
    },
    tooltip: {
      theme: 'light',
      style: {
        fontSize: '12px',
        fontFamily: 'inherit'
      },
      y: {
        formatter: function (val, opts) {
          const total = statusChartData.series ? statusChartData.series.reduce((a, b) => a + b, 0) : 0
          const percentage = total > 0 ? ((val / total) * 100).toFixed(1) : 0
          return '<div style="color: #556ee6; font-weight: 600; font-size: 14px;">' + val + ' RFQs</div>' +
                 '<div style="color: #74788d; font-size: 11px; margin-top: 2px;">(' + percentage + '% of total)</div>' +
                 '<div style="color: #28a745; font-size: 10px; margin-top: 2px;">Click to view details</div>'
        }
      }
    }
  }

  // Prepare timeline chart data with null safety - grouped by month
  const timelineData = (status_timeline || []).reduce((acc, item) => {
    const month = item.month || new Date().toISOString().slice(0, 7) // YYYY-MM format
    if (!acc[month]) {
      acc[month] = { month, total: 0 }
    }
    acc[month].total += parseInt(item.status_changes || 0)
    return acc
  }, {})

  const timelineArray = Object.values(timelineData).sort((a, b) => {
    const monthA = new Date(a.month + '-01' || '1970-01-01')
    const monthB = new Date(b.month + '-01' || '1970-01-01')
    return monthA - monthB
  })

  // Debug logging for timeline data
  console.log("Timeline data processing:", {
    status_timeline,
    timelineData,
    timelineArray,
    timelineArrayLength: timelineArray.length
  })
  
  const timelineChartOptions = {
    chart: {
      type: 'line',
      height: 300,
      zoom: {
        enabled: false
      },
      events: {
        dataPointSelection: handleTimelineChartClick,
        click: handleTimelineChartClick,
        markerClick: handleTimelineChartClick
      }
    },
    dataLabels: {
      enabled: false
    },
    markers: {
      size: 6,
      hover: {
        size: 8
      }
    },
    stroke: {
      curve: 'smooth',
      width: 3
    },
    title: {
      text: 'Status Changes Over Time',
      align: 'left'
    },
    grid: {
      row: {
        colors: ['#f3f3f3', 'transparent'],
        opacity: 0.5
      },
    },
    xaxis: {
      categories: timelineArray.map(item => item.month),
      title: {
        text: 'Month'
      }
    },
    yaxis: {
      title: {
        text: 'Number of Status Changes'
      }
    },
    colors: ['#556ee6'],
    tooltip: {
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        const month = w.globals.categoryLabels[dataPointIndex]
        const value = series[seriesIndex][dataPointIndex]
        return `
          <div class="custom-tooltip" style="padding: 10px; background: #fff; border: 1px solid #ccc; border-radius: 4px;">
            <strong>Month:</strong> ${month}<br/>
            <strong>Status Changes:</strong> ${value}<br/>
            <small style="color: #666;">Click to view details</small>
          </div>
        `
      }
    }
  }

  const timelineChartSeries = [{
    name: 'Status Changes',
    data: timelineArray.map(item => item.total)
  }]

  return (
    <div className="status-analytics fade-in">
      {/* Status Analytics Cards */}
      <Row className="mb-4">
        {overviewData ? (
          getCardData().map((card, index) => (
            <Col md={3} key={index}>
              <Card
                className={`metric-card ${card.color} clickable-card`}
                onClick={() => handleCardClick(card.type)}
                style={{ cursor: 'pointer', position: 'relative' }}
              >
                <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
                  <InfoButton
                    type={`status_analytics_${card.type}`}
                    title={`${card.title} - Business Logic & Calculation`}
                    size="sm"
                    
                  />
                </div>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <div className="metric-value" style={{color: '#495057', fontSize: '2rem', fontWeight: 'bold'}}>
                      {card.value}
                    </div>
                    <div className="metric-label" style={{color: '#6c757d', fontSize: '0.875rem'}}>
                      {card.title}
                      <small className="d-block text-muted" style={{fontSize: '0.7rem'}}>
                        <i className="bx bx-mouse me-1"></i>Click to view details
                      </small>
                    </div>
                  </div>
                  <div className="metric-icon">
                    <i className={card.icon}></i>
                  </div>
                </div>
              </Card>
            </Col>
          ))
        ) : (
          // Loading skeleton for cards
          [1, 2, 3, 4].map((index) => (
            <Col md={3} key={index}>
              <Card className="metric-card">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <div className="metric-value" style={{color: '#495057', fontSize: '2rem', fontWeight: 'bold'}}>
                      <Spinner size="sm" />
                    </div>
                    <div className="metric-label" style={{color: '#6c757d', fontSize: '0.875rem'}}>
                      Loading...
                      <small className="d-block text-muted" style={{fontSize: '0.7rem'}}>
                        Please wait
                      </small>
                    </div>
                  </div>
                  <div className="metric-icon">
                    <i className="bx bx-loader-alt bx-spin"></i>
                  </div>
                </div>
              </Card>
            </Col>
          ))
        )}
      </Row>

      {/* Additional Cards - Milestones and Tasks */}
      {(milestonesData || tasksData) && (
        <Row className="mb-4">
          {getAdditionalCardsData().map((card, index) => (
            <Col md={6} key={index}>
              <Card
                className={`metric-card ${card.color} clickable-card`}
                onClick={card.onClick}
                style={{ cursor: 'pointer', position: 'relative' }}
              >
                <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
                  <InfoButton
                    type={`status_analytics_${card.type}`}
                    title={`${card.title} - Business Logic & Calculation`}
                    size="sm"
                  />
                </div>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <div className="metric-value" style={{color: '#495057', fontSize: '2rem', fontWeight: 'bold'}}>
                      {card.value}
                    </div>
                    <div className="metric-label" style={{color: '#6c757d', fontSize: '0.875rem'}}>
                      {card.title}
                      {card.subtitle && (
                        <div className="text-success small mt-1" style={{fontSize: '0.75rem'}}>
                          <i className="bx bx-info-circle me-1"></i>
                          {card.subtitle}
                        </div>
                      )}
                      <small className="d-block text-muted" style={{fontSize: '0.7rem'}}>
                        <i className="bx bx-mouse me-1"></i>Click to view details
                      </small>
                    </div>
                  </div>
                  <div className="metric-icon">
                    <i className={card.icon}></i>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Charts */}
      <Row className="mb-4">
        <Col lg={6}>
          <Card className="dashboard-card">
            <CardBody>
              <CardTitle className="mb-4 d-flex justify-content-between align-items-center">
                <span>
                  <i className="bx bx-bar-chart-alt me-2"></i>
                  Status Distribution
                </span>
                <InfoButton
                  type="status_analytics"
                  title="Status Distribution Chart - Calculation Logic"
                  size="sm"
                />
              </CardTitle>
              <Chart
                options={statusChartOptions}
                series={[{
                  name: 'RFQ Count',
                  data: statusChartData.series
                }]}
                type="bar"
                height={400}
              />
            </CardBody>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="dashboard-card">
            <CardBody>
              <CardTitle className="mb-4 d-flex justify-content-between align-items-center">
                <span>
                  <i className="bx bx-line-chart me-2"></i>
                  Status Changes Timeline
                </span>
                <InfoButton
                  type="status_analytics"
                  title="Status Timeline Chart - Calculation Logic"
                  size="sm"
                />
              </CardTitle>
              {timelineArray.length > 0 ? (
                <div
                  className="timeline-chart-container"
                  onClick={handleTimelineContainerClick}
                >
                  <Chart
                    options={timelineChartOptions}
                    series={timelineChartSeries}
                    type="line"
                    height={400}
                  />
                </div>
              ) : (
                <div className="text-center py-4">
                  <i className="bx bx-line-chart display-4 text-muted"></i>
                  <p className="text-muted mt-2">No timeline data available</p>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Detailed Status Table */}
      {/* <Row>
        <Col lg={12}>
          <Card className="dashboard-card">
            <CardBody>
              <CardTitle className="mb-4 d-flex justify-content-between align-items-center">
                <span>
                  <i className="bx bx-table me-2"></i>
                  Detailed Status Analysis
                </span>
                <InfoButton
                  type="status_analytics"
                  title="Detailed Status Table - Calculation Logic"
                  size="sm"
                />
              </CardTitle>
              <div className="table-responsive">
                <Table className="table-nowrap mb-0">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Count</th>
                      <th>Percentage</th>
                      <th>Avg Days</th>
                      <th>Total Budget</th>
                      <th>Final Amount</th>
                      <th>Date Range</th>
                    </tr>
                  </thead>
                  <tbody>
                    {status_distribution.map((status, index) => (
                      <tr key={`status-row-${status.status_name || index}`}>
                        <td>
                          <Badge color={getStatusColor(status.status_name)} className="font-size-12">
                            {status.status_name}
                          </Badge>
                        </td>
                        <td>
                          <strong>{status.count}</strong>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="flex-grow-1">
                              <div className="progress progress-sm">
                                <div
                                  className="progress-bar bg-primary"
                                  role="progressbar"
                                  style={{ width: `${status.percentage}%` }}
                                ></div>
                              </div>
                            </div>
                            <div className="flex-shrink-0 ms-2">
                              <small className="text-muted">{status.percentage}%</small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="text-muted">
                            {status.avg_days_in_status ? `${status.avg_days_in_status} days` : 'N/A'}
                          </span>
                        </td>
                        <td>
                          <strong>{formatCurrency(status.total_budget)}</strong>
                        </td>
                        <td>
                          <strong className="text-success">
                            {formatCurrency(status.total_final_amount)}
                          </strong>
                        </td>
                        <td>
                          <small className="text-muted">
                            {status.earliest_rfq && status.latest_rfq ? (
                              <>
                                {new Date(status.earliest_rfq).toLocaleDateString()} - {' '}
                                {new Date(status.latest_rfq).toLocaleDateString()}
                              </>
                            ) : 'N/A'}
                          </small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row> */}

      {/* Status Card Modal */}
      <StatusCardModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        cardType={selectedCardType}
        filters={filters}
      />

      {/* Status Detail Modal */}
      <StatusDetailModal
        isOpen={statusModalOpen}
        toggle={() => setStatusModalOpen(false)}
        statusData={selectedStatus}
        filters={filters}
      />

      {/* Timeline Detail Modal */}
      <TimelineDetailModal
        isOpen={timelineModalOpen}
        toggle={() => setTimelineModalOpen(false)}
        dateData={selectedDate}
        filters={filters}
      />

      {/* Additional Milestones Modal */}
      <AdditionalMilestonesModal
        isOpen={milestonesModalOpen}
        toggle={() => setMilestonesModalOpen(false)}
        filters={filters}
        title="Additional Milestones Details"
      />

      {/* Additional Tasks Modal */}
      <AdditionalTasksModal
        isOpen={tasksModalOpen}
        toggle={() => setTasksModalOpen(false)}
        filters={filters}
        title="Additional Tasks Details"
      />
    </div>
  )
}

export default StatusAnalytics
